import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const ProjectDropdown = ({ userId, onProjectSelect, onProjectsLoaded }) => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize input value after component mounts and gets current values
  useEffect(() => {
    const current_project = localStorage.getItem('current_project');
    const default_project = localStorage.getItem('default_project');
    const initialValue = current_project || default_project || '';

    if (initialValue && initialValue !== 'null') {
      setInputValue(initialValue);
    }
  }, []); // Run once on mount

  // Listen for localStorage changes to update input value
  useEffect(() => {
    const handleStorageChange = () => {
      const current_project = localStorage.getItem('current_project');
      const default_project = localStorage.getItem('default_project');
      const newValue = current_project || default_project || '';

      if (newValue && newValue !== 'null' && newValue !== inputValue) {
        setInputValue(newValue);
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Also check periodically for changes within the same tab
    const interval = setInterval(handleStorageChange, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [inputValue]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update dropdown position when it becomes visible
  useEffect(() => {
    if (isDropdownVisible && inputRef.current) {
      const updatePosition = () => {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      };

      updatePosition();

      // Update position on scroll or resize
      const handleScroll = () => updatePosition();
      const handleResize = () => updatePosition();

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isDropdownVisible]);

  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true);
      setError('');

      const default_project = localStorage.getItem('default_project');
      console.log(`default_project in dropdown file  : ${default_project}`);

      try {
        const login_token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('user_id');

        const response = await fetch(appConfig.getUrl('/get_project_list'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${login_token}`
          },
          body: JSON.stringify({ user_id: userId })
        });

        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }

        const data = await response.json();

        console.log(`Project List  : ${data.project_list.length}`);

        if (data.status === 'Success') {
          setProjects(data.project_list);
          setFilteredProjects(data.project_list); // Initialize filtered projects with all projects

          // After projects are loaded, check if we need to update the input value
          const current_project = localStorage.getItem('current_project');
          const default_project = localStorage.getItem('default_project');
          const currentValue = current_project || default_project || '';

          if (currentValue && currentValue !== 'null') {
            setInputValue(currentValue);
          }

          // Call the callback to notify parent component that projects have been loaded
          if (onProjectsLoaded) {
            console.log('Calling onProjectsLoaded with:', data.project_list);
            onProjectsLoaded(data.project_list);

            // Give the parent component time to update localStorage, then check again
            setTimeout(() => {
              const updated_current = localStorage.getItem('current_project');
              const updated_default = localStorage.getItem('default_project');
              const updatedValue = updated_current || updated_default || '';

              if (updatedValue && updatedValue !== 'null' && updatedValue !== inputValue) {
                console.log('Updating input value to:', updatedValue);
                setInputValue(updatedValue);
              }
            }, 100);
          }
        } else {
          throw new Error(data.message || 'Failed to fetch projects');
        }
      } catch (error) {
        setError('Error fetching projects. Please try again.');
        console.error('Error fetching project list:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      fetchProjects();
    }
  }, [userId]); // Removed onProjectsLoaded from dependencies to prevent multiple calls

  // Debounced input handler
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue) {
        const filtered = projects.filter(project =>
          project.project_id.toLowerCase().includes(inputValue.toLowerCase())
        );
        setFilteredProjects(filtered);
      } else {
        setFilteredProjects(projects); // Show all projects when input is empty
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, projects]);

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
    const selectedProject = event.target.value;

    onProjectSelect(selectedProject);
  };

  const handleProjectSelect = (projectId) => {
    setInputValue(projectId);
    setIsDropdownVisible(false);
    onProjectSelect(projectId); // Add this line to properly update the parent
  };

  const toggleDropdown = () => {
    setIsDropdownVisible(!isDropdownVisible);
    if (!isDropdownVisible) {
      setFilteredProjects(projects); // Show all projects when opening dropdown
    }
  };

  // Dropdown content rendered as a portal
  const dropdownContent = isDropdownVisible && (
    <ul
      ref={dropdownRef}
      id="project-dropdown"
      className="bg-white border border-gray-300 rounded-md max-h-60 overflow-y-auto shadow-2xl"
      style={{
        position: 'absolute',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}
      role="listbox"
    >
      {filteredProjects.length > 0 ? (
        filteredProjects.map((project, index) => (
          <li
            key={index}
            onClick={() => handleProjectSelect(project.project_id)}
            className="px-4 py-2 cursor-pointer hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
            role="option"
            aria-selected={inputValue === project.project_id}
            tabIndex={0}
          >
            {project.project_id}
          </li>
        ))
      ) : (
        <li className="px-4 py-2 text-gray-500 text-center">
          No projects found
        </li>
      )}
    </ul>
  );

  return (
    <div className="relative w-full">
      <div className="relative" ref={inputRef}>
        <div className="flex">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => !isLoading && setIsDropdownVisible(true)}
            placeholder={!inputValue ? 'Type the project here or select from dropdown' : ''}
            className={`border rounded-l-md px-4 py-2 w-full ${
              error ? 'border-red-500' : 'border-gray-300'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            aria-label="Search projects"
            aria-expanded={isDropdownVisible}
            aria-controls="project-dropdown"
            aria-describedby={error ? 'error-message' : undefined}
          />
          <button
            onClick={toggleDropdown}
            className="px-3 bg-white border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Toggle dropdown"
          >
            {isDropdownVisible ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>
        </div>

        {isLoading && (
          <div className="absolute right-12 top-2.5">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        )}
      </div>

      {error && (
        <p id="error-message" className="mt-1 text-sm text-red-500">
          {error}
        </p>
      )}

      {/* Render dropdown using portal to document.body */}
      {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default ProjectDropdown;